import React, { useState, useEffect } from "react";
import {
  useTranslate,
  List,
  BooleanField,
  Datagrid,
  Create,
  TextInput,
  Edit,
  SimpleForm,
  TextField,
  ArrayField,
  SelectInput,
  NumberInput,
  useDataProvider,
  ArrayInput,
  SimpleFormIterator,
  Toolbar,
  ImageInput,
  ImageField,
  required,
  number,
  useRedirect,
  BooleanInput,
} from "react-admin";

import FileProvider from "../../Providers/FileProvider";
import Loading from "../Loading";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Content,
  HeaderRow,
  Title,
  FormGrid,
  ColumnCard,
  FieldGroup,
  HelperText,
  SectionBlock,
  SectionTitle,
  SectionHint,
  DaysGrid,
} from "./styles";

const useStyles = makeStyles((theme) => ({
  imageField: {
    "& img": { width: 115, height: 115, objectFit: "cover" },
  },
  paper: {
    padding: 20,
  },
}));

const handleImageUpload = async (file: any) => {
  const result = await FileProvider().create(file);
  //console.log("result", result);
  return result;
};

const transform = async (data: any, loading: any) => {
  loading(true);

  for (let index = 0; index < data?.photos?.length; index++) {
    const element = data?.photos[index];
    if (element.rawFile) {
      data.photos[index] = await handleImageUpload(element.rawFile);
    }
  }

  if (typeof data.category != "object") {
    data.category = {
      id: data.category,
    };
  }
  if (data.availableDays) {
    data.availableDays = {
      friday: data.availableDays?.friday ?? false,
      monday: data.availableDays?.monday ?? false,
      saturday: data.availableDays?.saturday ?? false,
      sunday: data.availableDays?.sunday ?? false,
      thursday: data.availableDays?.thursday ?? false,
      tuesday: data.availableDays?.tuesday ?? false,
      wednesday: data.availableDays?.wednesday ?? false,
    };
  }

  return {
    ...data,
  };
};

export const ProductList = (props: any) => {
  const translate = useTranslate();
  const classes = useStyles();
  const [data, setData] = useState<any>();
  const dataProvider = useDataProvider();

  const getData = React.useCallback(() => {
    dataProvider
      .getOne("configuration", {})
      .then((res: any) => {
        if (res) setData(res.data);
        //console.log("res", res.data);
      })
      .catch((error: any) => {
        //console.log(error);
      });
  }, [dataProvider, setData]);

  useEffect(() => {
    getData();
  }, [getData]);

  const getCurrency = (coin: string) => {
    switch (coin) {
      case "€":
        return "EUR";
      default:
        return "BRL";
    }
  };

  const CustomNumberField = (props: any) => (
    <span {...props}>
      {props.currency}{" "}
      {(props.record[props.source] / 100).toFixed(2).replace(".", ",")}
    </span>
  );

  return (
    <List
      {...props}
      title={translate(`custom.menu.${props.resource}`)}
      exporter={false}
      bulkActionButtons={false}
      sort={{ order: "ASC", field: "category" }}
    >
      <Datagrid rowClick="edit">
        <ImageField
          label={translate("custom.entities.product.photo")}
          className={classes.imageField}
          source="photos[0].url"
          sortable={false}
        />
        <TextField
          source="name"
          label={translate("custom.entities.product.name")}
        />
        <TextField
          source="description"
          label={translate("custom.entities.product.description")}
        />

        <TextField
          source="category.name"
          label={translate("custom.entities.product.category")}
          sortable={false}
        />
        <BooleanField source="isActive" label={"Habilitado"} sortable={true} />
        <ArrayField
          source="prices"
          label={translate("custom.entities.product.price")}
          sortable={false}
        >
          <Datagrid>
            <CustomNumberField
              label={null}
              source="value"
              currency={data?.currency}
            />
          </Datagrid>
        </ArrayField>
      </Datagrid>
    </List>
  );
};

const validateProduct = (values: any) => {
  const errors: any = {};

  if (!values.name) {
    errors.name = ["O produto precisa ter um nome!"];
  }

  if (!values.category) {
    errors.category = ["O produto precisa ter uma categoria!"];
  }

  return errors;
};

const validatePriceValue = [required(), number()];

export const ProductCreate = (props: any) => {
  const dataProvider = useDataProvider();
  const classes = useStyles();
  const redirect = useRedirect();

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<any>([]);
  const getCategories = React.useCallback(() => {
    dataProvider
      .getList("category", {
        pagination: { page: 1, perPage: 100 },
        sort: { order: "ASC", field: "order" },
      })
      .then((res: any) => {
        setCategories(res.data);
      })
      .catch((error: any) => {
        //console.log(error);
      });
  }, [dataProvider]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  const translate = useTranslate();

  return (
    <>
      <Loading show={loading} />
      <Create
        {...props}
        undoable={false}
        transform={(data: any) => transform(data, setLoading)}
        title={translate(`custom.menu.${props.resource}`)}
        onSuccess={() => {
          redirect("/product");
        }}
      >
        <SimpleForm validate={validateProduct}>
          <Container>
            <Content>
              <HeaderRow>
                <Title>Produto</Title>
              </HeaderRow>
              <FormGrid>
                <ColumnCard>
                  <SectionTitle>Fotos do produto</SectionTitle>
                  <HelperText>
                    Use imagens nítidas e bem iluminadas. A primeira foto será a
                    capa.
                  </HelperText>
                  <ImageInput
                    multiple={true}
                    source="photos"
                    label=""
                    accept={[".jpg", ".jpeg", ".png"]}
                    maxSize={10485760}
                    className={classes.imageField}
                  >
                    <ImageField source="url" />
                  </ImageInput>

                  <SectionTitle>Disponibilidade</SectionTitle>
                  <SectionHint>
                    Selecione em quais dias da semana o seu produto estará
                    disponível:
                  </SectionHint>
                  <DaysGrid>
                    <BooleanInput
                      source="availableDays.sunday"
                      defaultValue={true}
                      label={"Dom"}
                    />
                    <BooleanInput
                      source="availableDays.monday"
                      defaultValue={true}
                      label={"Seg"}
                    />
                    <BooleanInput
                      source="availableDays.tuesday"
                      defaultValue={true}
                      label={"Ter"}
                    />
                    <BooleanInput
                      source="availableDays.wednesday"
                      defaultValue={true}
                      label={"Qua"}
                    />
                    <BooleanInput
                      source="availableDays.thursday"
                      defaultValue={true}
                      label={"Qui"}
                    />
                    <BooleanInput
                      source="availableDays.friday"
                      defaultValue={true}
                      label={"Sex"}
                    />
                    <BooleanInput
                      source="availableDays.saturday"
                      defaultValue={true}
                      label={"Sab"}
                    />
                  </DaysGrid>
                </ColumnCard>

                <ColumnCard>
                  <SectionTitle>Informações básicas</SectionTitle>
                  <FieldGroup>
                    <TextInput
                      source="name"
                      label="Nome"
                      fullWidth={true}
                      required={true}
                    />
                    <TextInput
                      source="description"
                      label="Descrição"
                      fullWidth={true}
                    />
                    <SelectInput
                      choices={categories}
                      source="category.id"
                      label="Categoria"
                      name="category"
                      fullWidth={true}
                      required={true}
                    />
                    <SectionBlock>
                      <SectionHint>
                        Caso o produto esteja esgotado, desmarque esta opção
                      </SectionHint>
                      <BooleanInput
                        source="isActive"
                        label={"Habilitado"}
                        defaultValue={true}
                      />
                    </SectionBlock>
                  </FieldGroup>

                  <SectionTitle>Preços</SectionTitle>
                  <ArrayInput source="prices" label="">
                    <SimpleFormIterator>
                      <TextInput
                        source="title"
                        label="Unidade"
                        helperText="(Opcional) ex: KG, CM, Pequeno, Grande, Cento, etc."
                      />
                      <NumberInput
                        format={(v: number) => v / 100}
                        parse={(v: string) => parseFloat(v) * 100}
                        source="value"
                        helperText="* Obrigatório"
                        label="Valor"
                        step={0.01}
                        min={0}
                        validate={validatePriceValue}
                      />
                    </SimpleFormIterator>
                  </ArrayInput>
                </ColumnCard>
              </FormGrid>
            </Content>
          </Container>
        </SimpleForm>
      </Create>
    </>
  );
};

export const ProductEdit = (props: any) => {
  const dataProvider = useDataProvider();
  const [categories, setCategories] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const redirect = useRedirect();

  const getCategories = React.useCallback(() => {
    dataProvider
      .getList("category", {
        pagination: { page: 1, perPage: 100 },
        sort: { order: "ASC", field: "order" },
      })
      .then((res: any) => {
        if (res) setCategories(res.data);
      })
      .catch((error: any) => {
        setLoading(false);
      });
  }, [dataProvider]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  const translate = useTranslate();

  const ToolbarOverride = (props: any) => {
    return <Toolbar {...props} pristine={false} />;
  };

  return (
    <>
      <Loading show={loading} />
      <Edit
        {...props}
        undoable={false}
        transform={(data: any) => transform(data, setLoading)}
        title={translate(`custom.menu.${props.resource}`)}
        onSuccess={() => {
          redirect("/product");
        }}
      >
        <SimpleForm validate={validateProduct}>
          <Container>
            <Content>
              <HeaderRow>
                <Title>Produto</Title>
              </HeaderRow>
              <FormGrid>
                <ColumnCard>
                  <SectionTitle>Fotos do produto</SectionTitle>
                  <HelperText>
                    Use imagens nítidas e bem iluminadas. A primeira foto será a
                    capa.
                  </HelperText>
                  <ImageInput
                    multiple={true}
                    source="photos"
                    label=""
                    accept={[".jpg", ".jpeg", ".png"]}
                    maxSize={5000000}
                    className={classes.imageField}
                  >
                    <ImageField source="url" />
                  </ImageInput>

                  <SectionTitle>Disponibilidade</SectionTitle>
                  <SectionHint>
                    Selecione em quais dias da semana o seu produto estará
                    disponível:
                  </SectionHint>
                  <DaysGrid>
                    <BooleanInput source="availableDays.sunday" label={"Dom"} />
                    <BooleanInput source="availableDays.monday" label={"Seg"} />
                    <BooleanInput
                      source="availableDays.tuesday"
                      label={"Ter"}
                    />
                    <BooleanInput
                      source="availableDays.wednesday"
                      label={"Qua"}
                    />
                    <BooleanInput
                      source="availableDays.thursday"
                      label={"Qui"}
                    />
                    <BooleanInput source="availableDays.friday" label={"Sex"} />
                    <BooleanInput
                      source="availableDays.saturday"
                      label={"Sab"}
                    />
                  </DaysGrid>
                </ColumnCard>

                <ColumnCard>
                  <SectionTitle>Informações básicas</SectionTitle>
                  <FieldGroup>
                    <TextInput
                      source="name"
                      label="Nome"
                      fullWidth={true}
                      required={true}
                    />
                    <TextInput
                      source="description"
                      label="Descrição"
                      fullWidth={true}
                    />
                    <SelectInput
                      choices={categories}
                      source="category.id"
                      label="Categoria"
                      fullWidth={true}
                      required={true}
                    />
                    <SectionBlock>
                      <SectionHint>
                        Caso o produto esteja esgotado, desmarque esta opção
                      </SectionHint>
                      <BooleanInput source="isActive" label={"Habilitado"} />
                    </SectionBlock>
                  </FieldGroup>

                  <SectionTitle>Preços</SectionTitle>
                  <ArrayInput source="prices" label="">
                    <SimpleFormIterator>
                      <TextInput
                        source="title"
                        label="Unidade"
                        helperText="(Opcional) ex: KG, CM, Pequeno, Grande, Cento, etc."
                      />
                      <NumberInput
                        format={(v: number) => v / 100}
                        parse={(v: string) => parseFloat(v) * 100}
                        source="value"
                        helperText="* Obrigatório"
                        label="Valor"
                        step={0.01}
                        min={0}
                        validate={validatePriceValue}
                      />
                    </SimpleFormIterator>
                  </ArrayInput>
                </ColumnCard>
              </FormGrid>
            </Content>
          </Container>
        </SimpleForm>
      </Edit>
    </>
  );
};
